import React from 'react';
import { motion } from 'motion/react';
import { ScrollText, Leaf } from 'lucide-react';
import { LeafCluster } from '../components/WoodlandSVG';
import { fadeUp, pageTransition, viewportConfig } from '../components/AnimationVariants';

const sections = [
  {
    title: 'Acceptance of Terms',
    body: `By accessing the Woodlands Orthodontics website and using our services, you agree to be bound by these Terms of Service. If you do not agree with any part of these terms, please do not use our website or services.`,
  },
  {
    title: 'Our Services',
    body: `Woodlands Orthodontics provides specialist orthodontic care from our practice at 106 Woodlands, North Harrow, HA2 6EW. Our website is provided for informational purposes and to allow patients to contact us and request appointments. The information on this site does not constitute clinical advice and should not replace a consultation with a qualified orthodontist.`,
  },
  {
    title: 'Appointment Requests',
    body: `Submitting an appointment request through our website does not guarantee a confirmed booking. We will contact you within one business day to confirm your appointment. We reserve the right to decline appointment requests at our discretion.`,
  },
  {
    title: 'Patient Responsibilities',
    body: `Patients are responsible for:
• Providing accurate personal and medical information
• Attending scheduled appointments or cancelling with reasonable notice (minimum 24 hours)
• Following clinical advice provided by our orthodontists
• Settling fees in accordance with the agreed payment plan`,
  },
  {
    title: 'Fees and Payment',
    body: `Treatment fees will be confirmed at your initial consultation. We offer various payment options including NHS, private, and flexible finance plans. Full details are available on our Fees & Plans page or by contacting us directly. Outstanding balances may be subject to an administrative charge.`,
  },
  {
    title: 'Cancellations and Did Not Attend',
    body: `We ask that you give us at least 24 hours' notice if you need to cancel or reschedule an appointment. Repeated missed appointments without notice may result in removal from our patient list. We reserve appointment slots specifically for you and short-notice cancellations affect other patients' care.`,
  },
  {
    title: 'Intellectual Property',
    body: `All content on this website — including text, images, logos, and graphics — is the property of Woodlands Orthodontics and is protected by copyright. You may not reproduce, distribute, or use any content without our prior written permission.`,
  },
  {
    title: 'Limitation of Liability',
    body: `Woodlands Orthodontics endeavours to keep information on this website accurate and up to date, but makes no warranties regarding its completeness or accuracy. We are not liable for any loss or damage arising from reliance on information published on this site.`,
  },
  {
    title: 'Governing Law',
    body: `These terms are governed by and construed in accordance with the laws of England and Wales. Any disputes arising in connection with these terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.`,
  },
  {
    title: 'Changes to These Terms',
    body: `We may update these Terms of Service from time to time. Continued use of our website following any changes constitutes your acceptance of the revised terms. We recommend checking this page periodically.`,
  },
  {
    title: 'Contact Us',
    body: `For any questions regarding these terms, please contact us:

Woodlands Orthodontics
106 Woodlands, North Harrow, HA2 6EW
Phone: 020 8866 5556
Email: info@woodlandsorthodontics.co.uk`,
  },
];

export default function TermsOfService() {
  return (
    <motion.div {...pageTransition} className="overflow-x-hidden pt-24">

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-forest-dark to-forest overflow-hidden">
        <motion.div className="absolute right-0 top-0 w-64 opacity-15 hidden md:block"
          animate={{ y: [0, -10, 0] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}>
          <LeafCluster />
        </motion.div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.p className="text-sage-light text-sm uppercase tracking-widest font-medium mb-4 flex items-center justify-center gap-2"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
            <Leaf className="w-4 h-4" /> Legal
          </motion.p>
          <motion.h1 className="text-4xl md:text-5xl font-serif text-white font-semibold mb-5"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
            Terms of Service
          </motion.h1>
          <motion.p className="text-cream/75 text-lg leading-relaxed"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
            Please read these terms carefully before using our services.
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-cream">
        <div className="container-max max-w-3xl">
          <motion.div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-cream-deeper"
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <div className="flex items-center gap-3 mb-8 pb-6 border-b border-cream-deeper">
              <div className="w-10 h-10 rounded-xl bg-sage-pale flex items-center justify-center flex-shrink-0">
                <ScrollText className="w-5 h-5 text-forest" />
              </div>
              <p className="text-sm text-gray-500">Last updated: May 2025 · Woodlands Orthodontics</p>
            </div>

            <div className="space-y-10">
              {sections.map((s, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }} viewport={{ once: true }}>
                  <h2 className="font-serif text-forest text-xl font-semibold mb-3">{s.title}</h2>
                  <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{s.body}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}
